import React, { useCallback, useContext } from 'react'
import styled from 'styled-components'
import { CreationToolsArtboardContext } from '../context'
import DraggableComponent from '../draggable'
import { ResizeHandle } from '../draggable-ui'
import {
    ArtboardBaseLayerProps,
    ArtboardCornerPosition,
    ArtboardCropPosition,
    ArtboardDragPosition,
    ArtboardDragType,
} from '../types'

const Draggable = styled(DraggableComponent)<{ editable: boolean; boxShadowSize: number }>`
    border-radius: 0;
    box-shadow: 0 0 0 ${({ boxShadowSize }) => boxShadowSize}px rgba(0, 0, 0, 0.8);
    pointer-events: ${({ editable }) => (editable ? 'auto' : 'none')};

    &:hover {
        box-shadow: 0 0 0 ${({ boxShadowSize }) => boxShadowSize}px rgba(0, 0, 0, 0.8);
    }
`

interface Props extends ArtboardBaseLayerProps {
    aspectRatio?: number
    width: number
    height: number
    editable?: boolean
    onUpdate?: (crop: ArtboardCropPosition) => void
}

const CreationToolsArtboardCropLayer = ({
    defaultX,
    defaultY,
    defaultRotation,
    width,
    height,
    aspectRatio,
    editable = true,
    moveable,
    rotateable,
    resizeable,
    onUpdate,
}: Props) => {
    const { renderingContext, canvasScale, setCrop } = useContext(CreationToolsArtboardContext)
    const maxWidth = (renderingContext?.canvas.width || 0) / canvasScale
    const maxHeight = (renderingContext?.canvas.height || 0) / canvasScale
    const boxShadowSize = Math.max(maxWidth, maxHeight)

    const onDragUpdate = useCallback(
        ({ x, y, scaleX, scaleY }: ArtboardDragPosition) => {
            const cropWidth = Math.round(width * scaleX * canvasScale)
            const cropHeight = Math.round(height * scaleY * canvasScale)
            const crop = {
                x: Math.round(x * canvasScale - cropWidth / 2),
                y: Math.round(y * canvasScale - cropHeight / 2),
                width: cropWidth,
                height: cropHeight,
            }
            setCrop(crop)
            onUpdate && onUpdate(crop)
        },
        [width, height, canvasScale, setCrop, onUpdate]
    )

    return (
        <Draggable
            {...{
                defaultX,
                defaultY,
                defaultRotation,
                width,
                height,
                moveable,
                rotateable,
                resizeable,
                editable,
                boxShadowSize,
                selectable: false,
                lockAspectRatio: !!aspectRatio,
                onUpdate: onDragUpdate,
                bounds: {
                    left: 0,
                    top: 0,
                    right: maxWidth,
                    bottom: maxHeight,
                },
            }}
        >
            {resizeable && editable && (
                <>
                    <ResizeHandle position={ArtboardCornerPosition.TopLeft} data-drag-type={ArtboardDragType.Resize} />
                    <ResizeHandle position={ArtboardCornerPosition.TopRight} data-drag-type={ArtboardDragType.Resize} />
                    <ResizeHandle
                        position={ArtboardCornerPosition.BottomLeft}
                        data-drag-type={ArtboardDragType.Resize}
                    />
                    <ResizeHandle
                        position={ArtboardCornerPosition.BottomRight}
                        data-drag-type={ArtboardDragType.Resize}
                    />
                </>
            )}
        </Draggable>
    )
}

export default CreationToolsArtboardCropLayer
